/* eslint-disable no-console */
import { EventType } from '@portal/enum/analytics';
import ErrorsMessages from '@portal/enum/errors';
import {
  DetailType,
  TransactionFlow,
  TransactionType,
} from '@portal/models/enumerators';
import { sendEvent } from '@portal/services/firebase';
import { isAxiosError } from 'axios';

import { PixStatusString } from '@portal/enum/PixStatus';
import PixApi from '~/repositories/pix';
import * as MessageService from '~/services/message';

import { PixType } from '@portal/enum/pix';
import { getPixKeyType } from '@portal/utils/pixKeyType';

import {
  PIX_CANCEL_PORTABILITY_SUCCESS,
  PIX_DELETE_SUCCESS,
  PIX_GET_ALL_KEYS,
  PIX_OWNERSHIP_CLAIM_SUCCESS,
  PIX_PORTABILITY_SUCCESS,
  PIX_REGISTER_DETAILS,
  PIX_VERIFY_CODE_DETAILS,
  SET_PENDING_PIX_KEY_PORTABILITIES,
  SET_PIX_PSPS,
  SET_PIX_PSP_ERROR,
} from './actionTypes';
import { setDetails, setDetailsError } from './details';
import { decreaseLoading, increaseLoading } from './loading';

const isFraudRestrictionError = (err: unknown): boolean => {
  console.log('Checking if error is fraud restriction error', err);
  if (isAxiosError(err)) {
    const data: { errorCode?: string } | undefined = err.response?.data;
    if (data?.errorCode === ErrorsMessages.USER_OR_ACCOUNT_FRAUD_RESTRICTION) {
      return true;
    }
    if (data?.errorCode?.includes('FraudRelatedRestriction')) return true;
  }
  if (!(err instanceof Error)) return false;
  return (
    err.message === ErrorsMessages.USER_OR_ACCOUNT_FRAUD_RESTRICTION ||
    err.message.includes('FraudRelatedRestriction')
  );
};

const handleFraudRestrictionError = (err: unknown): boolean => {
  if (!isFraudRestrictionError(err)) return false;
  MessageService.error('PIX.USER_OR_ACCOUNT_FRAUD_RESTRICTION', {
    originalError: err as Error,
  });
  return true;
};

export const setPixPSPsError = (payload: boolean) => ({
  type: SET_PIX_PSP_ERROR,
  payload,
});

export const registerPixKey =
  (body: models.RegisterPixKey) => async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const payload = await PixApi.registerPixKey(body);
      sendEvent(EventType.CREATE_PIX_KEY);
      const requiresVerification =
        body.pixType === PixType.PHONE || body.pixType === PixType.EMAIL;

      if (
        payload.statusString ===
        PixStatusString.IM_CLAIMER_PENDING_OWNER_GIVEWAY
      ) {
        MessageService.success('PAGES.HOME.PIX.PORTABILITY_REQUESTED_SUCCESS');
        dispatch({ type: PIX_VERIFY_CODE_DETAILS, payload: true });
        dispatch({ type: PIX_PORTABILITY_SUCCESS, payload: true });
      } else if (!requiresVerification) {
        MessageService.success('PAGES.HOME.PIX.KEY_CREATED_SUCCESS');
      }

      dispatch({
        payload,
        type: PIX_REGISTER_DETAILS,
      });
    } catch (err) {
      if (handleFraudRestrictionError(err)) return;

      if (isAxiosError(err)) {
        const data: { errorCode?: string } | undefined = err.response?.data;
        if (data?.errorCode === ErrorsMessages.INVALID_TOKEN) {
          MessageService.error('PIX.INVALID_TOKEN', { originalError: err });
          return;
        }
        if (data?.errorCode === ErrorsMessages.CLAIM_ALREADY_EXISTS_FOR_KEY) {
          MessageService.error('PIX.CLAIM_ALREADY_EXISTS_FOR_KEY', {
            originalError: err,
          });
          return;
        }

        MessageService.error(`PIX.${err.message}`, { originalError: err });
        return;
      }

      if (err instanceof Error) {
        MessageService.error(`PIX.${err.message}`, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const requestPortabilityCode =
  (requestData: models.RegisterPixKey) => async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const payload = await PixApi.registerPixKey(requestData);

      if (
        payload.statusString ===
        PixStatusString.IM_CLAIMER_PENDING_OWNER_GIVEWAY
      ) {
        MessageService.success('PAGES.HOME.PIX.PORTABILITY_REQUESTED_SUCCESS');
        dispatch({ type: PIX_VERIFY_CODE_DETAILS, payload: true });
        dispatch({ type: PIX_PORTABILITY_SUCCESS, payload: true });
        await dispatch(getAllPixKeys());
      }

      dispatch({
        payload,
        type: PIX_REGISTER_DETAILS,
      });
      return payload;
    } catch (err) {
      if (handleFraudRestrictionError(err)) throw err;

      if (isAxiosError(err)) {
        const data: { errorCode?: string } | undefined = err.response?.data;
        if (data?.errorCode === ErrorsMessages.INVALID_TOKEN) {
          MessageService.error('PIX.INVALID_TOKEN');
          throw err;
        }
        if (data?.errorCode === ErrorsMessages.CLAIM_ALREADY_EXISTS_FOR_KEY) {
          MessageService.error('PIX.CLAIM_ALREADY_EXISTS_FOR_KEY', {
            originalError: err,
          });
          throw err;
        }
        MessageService.error(`PIX.${err.message}`, { originalError: err });
        throw err;
      }
      if (err instanceof Error) {
        MessageService.error(`PIX.${err.message}`, { originalError: err });
      }
      throw err;
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const confirmPortability =
  (requestData: models.RegisterPixKey) => async (dispatch) => {
    dispatch(increaseLoading());

    try {
      await PixApi.confirmPortability(requestData);
      await dispatch(getAllPixKeys());

      const successKey = requestData.createReivindication
        ? 'PAGES.HOME.PIX.PORTABILITY_REQUESTED_SUCCESS'
        : 'PAGES.HOME.PIX.KEY_CREATED_SUCCESS';
      MessageService.success(successKey);
      dispatch({
        payload: true,
        type: PIX_VERIFY_CODE_DETAILS,
      });
    } catch (err) {
      if (handleFraudRestrictionError(err)) throw err;

      if (isAxiosError(err)) {
        const data: { errorCode?: string } | undefined = err.response?.data;
        if (data?.errorCode === ErrorsMessages.CLAIM_ALREADY_EXISTS_FOR_KEY) {
          MessageService.error('PIX.CLAIM_ALREADY_EXISTS_FOR_KEY', {
            originalError: err,
          });
          throw err;
        }
      }
      if (err instanceof Error) {
        if (err.message === ErrorsMessages.INVALID_TOKEN) {
          MessageService.error('PIX.INVALID_TOKEN', {
            originalError: err,
          });

          return;
        }

        if (err.message === ErrorsMessages.TRANSACTION_NOT_APPROVED) {
          MessageService.error('PIX.INVALID_PIN', {
            originalError: err,
          });

          return;
        }

        MessageService.error(`PIX.${ErrorsMessages.PORTABILITY_ERROR}`, {
          originalError: err,
        });
      }

      throw err;
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cleanRegisterPixKey = () => async (dispatch) => {
  dispatch({
    payload: null,
    type: PIX_REGISTER_DETAILS,
  });
  dispatch({
    payload: false,
    type: PIX_VERIFY_CODE_DETAILS,
  });
  dispatch({
    payload: false,
    type: PIX_PORTABILITY_SUCCESS,
  });
  dispatch({
    payload: false,
    type: PIX_CANCEL_PORTABILITY_SUCCESS,
  });
  dispatch({
    payload: false,
    type: PIX_OWNERSHIP_CLAIM_SUCCESS,
  });
};

export const getAllPixKeys = () => async (dispatch) => {
  dispatch(increaseLoading());

  try {
    const payload = await PixApi.getAllPixKeys();
    const normalizedPayload = Array.isArray(payload)
      ? payload.map((key) => ({
          ...key,
        }))
      : payload;
    dispatch({
      payload: normalizedPayload,
      type: PIX_GET_ALL_KEYS,
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error('GET_PIX_KEYS_ERROR');
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const deletePortability =
  (key: string) => async (dispatch, getState) => {
    dispatch(increaseLoading());

    try {
      await PixApi.deletePortability(key);

      const current = getState().user?.pendingPixKeyPortabilities ?? [];
      dispatch({
        type: SET_PENDING_PIX_KEY_PORTABILITIES,
        payload: current.filter((p) => p.key !== key),
      });

      await dispatch(getAllPixKeys());

      MessageService.success('PAGES.HOME.PIX.CANCEL_PORTABILITY_SUCCESS');

      dispatch({
        payload: true,
        type: PIX_CANCEL_PORTABILITY_SUCCESS,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const portabilityResponse =
  (key: string, confirm: boolean) => async (dispatch) => {
    dispatch(increaseLoading());

    try {
      await PixApi.portabilityResponse(key, confirm);
      await dispatch(getAllPixKeys());

      dispatch({
        payload: true,
        type: PIX_OWNERSHIP_CLAIM_SUCCESS,
      });

      MessageService.success(
        confirm
          ? 'PAGES.HOME.PIX.PORTABILITY_CONFIRM_SUCCESS'
          : 'PAGES.HOME.PIX.CANCEL_PORTABILITY_SUCCESS'
      );
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const deletePixKey = (key: string) => async (dispatch) => {
  dispatch(increaseLoading());

  try {
    await PixApi.deletePixKey(key);
    sendEvent(EventType.DELETE_PIX_KEY);

    dispatch({
      payload: key,
      type: PIX_DELETE_SUCCESS,
    });

    setTimeout(() => {
      dispatch(getAllPixKeys());
    }, 2000);
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const decodePixQrCode = (content: string) => async (dispatch) => {
  dispatch(increaseLoading());

  try {
    const transaction = await PixApi.decodePixQrCode(content);
    sendEvent(EventType.READ_PIX_QR_CODE);

    dispatch(
      setDetails({
        transaction,
        type: DetailType.NEW,
        error: null,
      })
    );
  } catch (err) {
    if (err instanceof Error) {
      if (err.message === ErrorsMessages.PIX_INVALID_PARAMS) {
        MessageService.error('PIX_INVALID_PARAMS');
      } else {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const listPSPs = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload = await PixApi.getPSPs();
    dispatch({
      type: SET_PIX_PSPS,
      payload,
    });
  } catch (err) {
    setPixPSPsError(true);
    MessageService.error('PIX_PSPS_ERROR');
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getPixInfo = (key: string, amount: number) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const digits = key.replace(/\D/g, '');
    const keyType = getPixKeyType(key);

    const normalizedKey =
      keyType === PixType.PHONE && !key.startsWith('+') ? `+55${digits}` : key;
    const pixInfo = await PixApi.getPixInfo(normalizedKey);
    dispatch(
      setDetails({
        transaction: {
          amount,
          type: TransactionType.PIX,
          flow: TransactionFlow.CASH_OUT,
          pixInfo,
        },
        type: DetailType.NEW,
        error: null,
      })
    );
  } catch (err) {
    if (err instanceof Error) {
      if (err.message === ErrorsMessages.PIX_KEY_NOT_FOUND) {
        MessageService.error('PIX.PIX_KEY_NOT_FOUND');
      } else if (err.message === ErrorsMessages.PIX_KEY_FRAUD) {
        MessageService.error('PIX.PIX_KEY_FRAUD');
      } else {
        dispatch(setDetailsError(err));
      }
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getPixSetup = () => async (dispatch) => {
  try {
    const { pendingPortabilities } = await PixApi.getPixSetup();
    dispatch({
      type: SET_PENDING_PIX_KEY_PORTABILITIES,
      payload: pendingPortabilities ?? [],
    });
  } catch {
    dispatch({
      type: SET_PENDING_PIX_KEY_PORTABILITIES,
      payload: [],
    });
  }
};

export const portabilityConfirmSetup =
  (key: string, confirm: boolean) => async (dispatch, getState) => {
    dispatch(increaseLoading());
    try {
      await PixApi.portabilityConfirm(key, confirm);

      const current = getState().user?.pendingPixKeyPortabilities ?? [];
      dispatch({
        type: SET_PENDING_PIX_KEY_PORTABILITIES,
        payload: current.filter((p) => p.key !== key),
      });

      MessageService.success('PAGES.HOME.PIX.PORTABILITY_CONFIRM_SUCCESS');

      setTimeout(() => {
        dispatch(getAllPixKeys());
      }, 1000);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
